import { isShapeType } from "../../../util/shapeTypeMapper";
import { convertImageToDataUrl } from "./exportUtils";

/** Escape text for use inside SVG/XML */
function escapeXml(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Normalize custom shape HTML tags to standard HTML for SVG export */
function normalizeShapeHtml(html) {
  if (html == null || typeof html !== "string") return html;
  return html
    .replace(/<bold>/gi, "<b>")
    .replace(/<\/bold>/gi, "</b>")
    .replace(/<italic>/gi, "<i>")
    .replace(/<\/italic>/gi, "</i>")
    .replace(/<underline>/gi, '<span style="text-decoration:underline">')
    .replace(/<\/underline>/gi, "</span>")
    .replace(/<highlight>/gi, '<span style="background-color:#ffff00">')
    .replace(/<\/highlight>/gi, "</span>");
}

/**
 * Helper to get SVG transform string from Konva node
 */
export function getSvgTransform(node) {
  const {
    x = 0,
    y = 0,
    scaleX = 1,
    scaleY = 1,
    rotation = 0,
    offsetX = 0,
    offsetY = 0,
  } = node.attrs;

  const translate = `translate(${x - offsetX}, ${y - offsetY})`;
  const rotate = rotation !== 0 ? `rotate(${rotation})` : "";
  const scale =
    scaleX !== 1 || scaleY !== 1 ? `scale(${scaleX}, ${scaleY})` : "";

  return `transform="${[translate, rotate, scale]
    .filter(Boolean)
    .join(" ")}"`;
}

/**
 * Processes a Konva node and adds SVG representation to svgParts
 */
export function processNode(node, svgParts, defsParts) {
  const type = node.getClassName();
  const { fill, stroke, strokeWidth, opacity, visible = true } = node.attrs;

  if (
    !visible ||
    type === "Transformer" ||
    type === "Arrow" ||
    type === "DoubleSidedArrow"
  )
    return;

  const commonAttrs = `fill="${fill || "none"}" stroke="${
    stroke || "none"
  }" stroke-width="${strokeWidth || 0}" ${
    opacity !== undefined ? `opacity="${opacity}"` : ""
  }`;

  if (isShapeType(type, "Rect")) {
    const { width, height, name: rectName } = node.attrs;

    // Skip background rectangles that cover the entire stage area
    const stage = node.getStage();
    if (
      stage &&
      width >= stage.width() &&
      height >= stage.height() &&
      (fill === "#ffffff" || fill === "white")
    ) {
      return; // Skip this background rectangle
    }

    // Skip any large white rectangles that might be background elements
    if (
      width > 1000 &&
      height > 500 &&
      (fill === "#ffffff" || fill === "white") &&
      !stroke
    ) {
      return; // Skip large white background rectangles
    }

    // Skip rectangles with names indicating they are background elements
    if (
      rectName &&
      (rectName.includes("background") ||
        rectName.includes("bg") ||
        rectName.includes("stage"))
    ) {
      return; // Skip background rectangles
    }

    // Skip the Background component specifically (it has id="bg")
    if (node.id() === "bg") {
      return; // Skip the Background component
    }

    // Make text-box backgrounds transparent in export to prevent covering text; other rectangles keep fill.
    const exportFill =
      rectName === "text-box-container" ? "none" : fill || "none";

    const rectAttrs = `fill="${exportFill}" stroke="${
      stroke || "none"
    }" stroke-width="${strokeWidth || 0}" ${
      opacity !== undefined ? `opacity="${opacity}"` : ""
    }`;

    // svgParts.push(
    //   `<g ${getSvgTransform(
    //     node
    //   )}><rect x="0" y="0" width="${width}" height="${height}" ${rectAttrs} /></g>`
    // );
  } else if (isShapeType(type, "Circle")) {
    const { radius } = node.attrs;
    svgParts.push(
      `<g ${getSvgTransform(
        node
      )}><circle cx="0" cy="0" r="${radius}" ${commonAttrs} /></g>`
    );
  } else if (isShapeType(type, "Text")) {
    const { text, fontSize = 16, fontFamily, width: maxW, wrap } = node.attrs;

    const familyName = fontFamily || "Roboto";
    const familyAttr = `font-family="${
      familyName.includes(" ") ? `'${familyName}'` : familyName
    }"`;

    let svgTextContent = "";

    if (wrap === "word" && maxW) {
      // Word wrap manually using canvas measurement
      const ctx = document.createElement("canvas").getContext("2d");
      ctx.font = `${fontSize}px ${familyName}`;
      const words = text.split(/\s+/);
      let line = "";
      const linesArr = [];
      words.forEach((w) => {
        const testLine = line ? line + " " + w : w;
        const { width: testWidth } = ctx.measureText(testLine);
        if (testWidth > maxW - 2 && line) {
          linesArr.push(line);
          line = w;
        } else {
          line = testLine;
        }
      });
      if (line) linesArr.push(line);

      // Build tspans
      const lineHeight = fontSize * 1.2;
      svgTextContent = linesArr
        .map((ln, idx) => {
          const dy = idx === 0 ? 0 : lineHeight;
          return `<tspan x="0" dy="${dy}">${ln}</tspan>`;
        })
        .join("");
    } else {
      svgTextContent = text;
    }
  } else if (type === "Group") {
    // Process Group nodes but skip HTML text content to prevent duplication
    // Text elements are processed separately via processTextElement
    const groupId = node.id();

    svgParts.push(`<g ${getSvgTransform(node)}>`);

    // Process Konva children (rects, circles, etc.) but skip HTML text
    node.getChildren().forEach((child) => {
      const childType = child.getClassName();
      // Skip HTML nodes that contain text content
      if (childType !== "Html") {
        processNode(child, svgParts, defsParts);
      }
    });

    svgParts.push(`</g>`);
  } else if (isShapeType(type, "Image")) {
    // Images are emitted from the images array in convertStageToSVG to ensure they appear
    // (node.image() can be null; state is the source of truth). Skip here to avoid duplicates.
    return;
  } else if (isShapeType(type, "Line")) {
    const { points, dash, lineCap, lineJoin } = node.attrs;

    const pointPairs = [];
    for (let i = 0; i < points.length; i += 2) {
      pointPairs.push(`${points[i]},${points[i + 1]}`);
    }

    let lineAttrs = `points="${pointPairs.join(" ")}" stroke="${
      stroke || "black"
    }" stroke-width="${strokeWidth || 1}" fill="none"`;
    if (dash?.length) lineAttrs += ` stroke-dasharray="${dash.join(" ")}"`;
    if (lineCap) lineAttrs += ` stroke-linecap="${lineCap}"`;
    if (lineJoin) lineAttrs += ` stroke-linejoin="${lineJoin}"`;
    if (opacity !== undefined) lineAttrs += ` opacity="${opacity}"`;

    svgParts.push(
      `<g ${getSvgTransform(node)}><polyline ${lineAttrs} /></g>`
    );
  } else if (type === "Arrow") {
    const {
      points,
      pointerLength = 10,
      pointerWidth = 10,
      stroke = "black",
      strokeWidth = 2,
      pointerAtBeginning = false,
      pointerAtEnding = true,
      x = 0,
      y = 0,
    } = node.attrs;

    const getArrowPath = (x1, y1, x2, y2, strokeWidth = 2) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = Math.max(10, strokeWidth * 2);
      const arrowWidth = Math.max(5, strokeWidth);
      const tipX = x2;
      const tipY = y2;
      const leftX =
        tipX - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
      const leftY =
        tipY - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
      const rightX =
        tipX - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
      const rightY =
        tipY - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);
      return `M ${tipX},${tipY} L ${leftX},${leftY} L ${rightX},${rightY} Z`;
    };

    // Create the polyline (main arrow line) - points are already relative to arrow position
    const pointPairs = [];
    for (let i = 0; i < points.length; i += 2) {
      pointPairs.push(`${points[i]},${points[i + 1]}`);
    }

    let lineAttrs = `points="${pointPairs.join(" ")}" 
      stroke="${stroke}" 
      stroke-width="${strokeWidth}" 
      fill="none"
      vector-effect="non-scaling-stroke"`;
    if (opacity !== undefined) lineAttrs += ` opacity="${opacity}"`;

    // Collect arrowheads
    const arrowPaths = [];

    // Add head at the end
    if (pointerAtEnding && points.length >= 4) {
      const len = points.length;
      arrowPaths.push(
        getArrowPath(
          points[len - 4],
          points[len - 3],
          points[len - 2],
          points[len - 1],
          strokeWidth
        )
      );
    }

    // Add head at the beginning
    if (pointerAtBeginning && points.length >= 4) {
      arrowPaths.push(
        getArrowPath(points[2], points[3], points[0], points[1], strokeWidth)
      );
    }

    // SVG output - use transform to position the arrow, points are relative
    svgParts.push(`<g ${getSvgTransform(node)}>
    <polyline ${lineAttrs} />
    ${arrowPaths
      .map(
        (path) =>
          `<path d="${path}" fill="${stroke}" stroke="none"${
            opacity !== undefined ? ` opacity="${opacity}"` : ""
          } />`
      )
      .join("\n")}
   </g>`);
  } else if (type === "DoubleSidedArrow" || type === "double_sided_arrow") {
    const {
      points,
      pointerLength = 10,
      pointerWidth = 10,
      stroke = "black",
      strokeWidth = 2,
      pointerAtBeginning = true, // Double-sided arrows have heads at both ends
      pointerAtEnding = true,
      x = 0,
      y = 0,
    } = node.attrs;

    const getArrowPath = (x1, y1, x2, y2, strokeWidth = 2) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = Math.max(10, strokeWidth * 2); // pointerLength
      const arrowWidth = Math.max(5, strokeWidth); // pointerWidth / 2
      const tipX = x2;
      const tipY = y2;
      const leftX =
        tipX - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
      const leftY =
        tipY - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
      const rightX =
        tipX - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
      const rightY =
        tipY - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);
      return `M ${tipX},${tipY} L ${leftX},${leftY} L ${rightX},${rightY} Z`;
    };

    // Create the polyline (main arrow line) - points are already relative to arrow position
    const pointPairs = [];
    for (let i = 0; i < points.length; i += 2) {
      pointPairs.push(`${points[i]},${points[i + 1]}`);
    }

    let lineAttrs = `points="${pointPairs.join(" ")}" 
      stroke="${stroke}" 
      stroke-width="${strokeWidth}" 
      fill="none"
      vector-effect="non-scaling-stroke"`;
    if (opacity !== undefined) lineAttrs += ` opacity="${opacity}"`;

    // Collect arrowheads
    const arrowPaths = [];

    // Add head at the end
    if (pointerAtEnding && points.length >= 4) {
      const len = points.length;
      arrowPaths.push(
        getArrowPath(
          points[len - 4],
          points[len - 3],
          points[len - 2],
          points[len - 1],
          strokeWidth
        )
      );
    }

    // Add head at the beginning (double-sided arrows have heads at both ends)
    if (points.length >= 4) {
      arrowPaths.push(
        getArrowPath(points[2], points[3], points[0], points[1], strokeWidth)
      );
    }

    // SVG output - use transform to position the arrow, points are relative
    svgParts.push(`<g ${getSvgTransform(node)}>
    <polyline ${lineAttrs} />
    ${arrowPaths
      .map(
        (path) =>
          `<path d="${path}" fill="${stroke}" stroke="none"${
            opacity !== undefined ? ` opacity="${opacity}"` : ""
          } />`
      )
      .join("\n")}
   </g>`);
  } else if (type === "Layer") {
    // Process layer children
    node
      .getChildren()
      .forEach((child) => processNode(child, svgParts, defsParts));
  }
}

/**
 * Processes a text element and adds SVG representation to svgParts
 */
export function processTextElement(
  text,
  svgParts,
  defsParts,
  processHtmlContentWithFormatting
) {
  if (!text || !text.x || !text.y) return;

  // Use original coordinates to match arrows and other elements
  const adjustedX = text.x;
  const adjustedY = text.y;

  const textBlockWidth = text.width || 250;
  const fontSize = text.fontSize || 14;
  const fontFamily = "Roboto";
  const familyAttr = `font-family="${fontFamily}"`;

  let currentY = 25; // Increased initial padding for better spacing

  // Create a group for this text element
  const groupTransform = `transform="translate(${adjustedX}, ${adjustedY})"`;
  const groupStart = `<g ${groupTransform}>`;
  const groupEnd = `</g>`;

  let groupContent = [];
  const leftPadding = 15; // Add left padding for text content

  // Process Content section (HTML content from text element)
  if (text.content && text.content.trim()) {
    const contentResult = processHtmlContentWithFormatting(
      text.content,
      currentY,
      text.fontSize || 14
    );
    if (contentResult.content) {
      groupContent.push(contentResult.content);
      currentY += contentResult.height + 15;
    }
  }

  // Process PartsByType section
  if (text.partsByType) {
    Object.entries(text.partsByType)
      .sort((a, b) => (a[1].order || 9999) - (b[1].order || 9999))
      .forEach(([typeName, typeData]) => {
        if (!typeData.parts || typeData.parts.length === 0) return;

        // Type heading
        const typeFontSize = text.textStyles?.type?.fontSize || 16;
        const typeColor = text.textStyles?.type?.color || "black";
        groupContent.push(
          `<text x="${leftPadding}" y="${currentY}" font-size="${typeFontSize}" ${familyAttr} font-weight="bold" fill="${typeColor}">${typeName}</text>`
        );
        currentY += typeFontSize + 5;

        // Parts
        typeData.parts.forEach((partObj) => {
          // Skip null or undefined partObj entries
          if (!partObj) return;
          
          const partFontSize =
            text.fontSize || text.textStyles?.part?.fontSize || 14;
          // Only show qty/unit if qty is not blank and unit is not 'Nos' or blank
          let qtyUnitStr = "";
          if (partObj.qty && partObj.qty.trim() !== "") {
            if (
              partObj.unit &&
              partObj.unit.trim() !== "" &&
              partObj.unit.trim() !== "Nos"
            ) {
              qtyUnitStr = ` - Qty-${partObj.qty} ${partObj.unit}`;
            } else {
              qtyUnitStr = ` - Qty-${partObj.qty}`;
            }
          }
          // Ensure part HTML is valid and wrapped properly
          const partHtml = `<p>${partObj.part || ""}${qtyUnitStr}</p>`;
          const partResult = processHtmlContentWithFormatting(
            partHtml,
            currentY,
            partFontSize
          );

          if (partResult.content) {
            const paddedPartContent = partResult.content.replace(
              /<text x="\d+"/g,
              `<text x="${leftPadding}"`
            );
            groupContent.push(paddedPartContent);
            // Add spacing for multi-line content
            currentY += partResult.height + 8; // 8px margin after part
          }

          // Part note
          if (partObj.note && partObj.note.trim()) {
            const noteFontSize =
              text.fontSize || text.textStyles?.note?.fontSize || 14;
            const noteResult = processHtmlContentWithFormatting(
              partObj.note,
              currentY + 5,
              noteFontSize // Pass correct font size
            );
            if (noteResult.content) {
              groupContent.push(noteResult.content);
              // Add spacing for multi-line note
              currentY += noteResult.height + 15; // 15px margin after note
            }
          }
        });

        currentY += 5; // Space after type section
      });
  }

  // Process Task section
  if (text.task && text.task.trim()) {
    currentY += 8; // Section margin

    // Task label
    groupContent.push(
      `<text x="15" y="${currentY}" font-size="18" ${familyAttr} font-weight="bold" fill="black">Task</text>`
    );
    currentY += 23;

    // Task content
    const taskResult = processHtmlContentWithFormatting(text.task, currentY);
    if (taskResult.content) {
      // Add left padding to each line of the task content
      const paddedTaskContent = taskResult.content.replace(
        /<text x="\d+"/g,
        '<text x="15"'
      );
      groupContent.push(paddedTaskContent);
      currentY += taskResult.height + 15;
    }
  }

  // Process Note section
  if (text.note && text.note.trim()) {
    currentY += 8; // Section margin

    // Note label
    groupContent.push(
      `<text x="15" y="${currentY}" font-size="18" ${familyAttr} font-weight="bold" fill="black">Note</text>`
    );
    currentY += 23;

    // Note content
    // Use processHtmlContentWithFormatting to render HTML as SVG
    const noteResult = processHtmlContentWithFormatting(text.note, currentY);
    if (noteResult.content && noteResult.content.trim()) {
      groupContent.push(noteResult.content);
      currentY += noteResult.height + 15;
    }
  }

  // Add the text group to SVG if it has content
  if (groupContent.length > 0) {
    // Add background rectangle and border
    const bgWidth = textBlockWidth;
    const bgHeight = Math.max(currentY + 10, 50); // Ensure minimum height
    // Remove border: set stroke to 'none' and stroke-width to 0
    let backgroundRect = `<rect x="0" y="0" width="${bgWidth}" height="${bgHeight}" fill="white" stroke="none" stroke-width="0" rx="5"/>`;
    svgParts.push(
      groupStart + backgroundRect + groupContent.join("") + groupEnd
    );
  }
}

/**
 * Converts a Konva stage to SVG string
 */
export function convertStageToSVG({
  stage,
  arrows,
  doubleSidedArrows,
  texts,
  circles,
  rectangles,
  videos,
  images = [],
  processHtmlContentWithFormatting,
}) {
  const svgParts = [];
  const defsParts = [];

  // Get stage dimensions
  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Add vertical spacing at the top (e.g., 40px)
  const topSpacing = 40;
  const leftSpacing = 0;
  const svgWidth = stageWidth + 200;
  const svgHeight = stageHeight + topSpacing;

  // Start SVG
  svgParts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">`
  );

  // Add defs for any patterns, gradients, etc.
  svgParts.push(`<defs>
      <style>
        .roboto-text {
          font-family: 'Roboto', Arial, sans-serif;
          font-size: 14px;
          line-height: 1.4;
          color: #000;
        }
        /* Ensure proper text spacing and alignment */
        text {
          font-family: 'Roboto', Arial, sans-serif;
          white-space: pre;
          line-height: 1.4;
        }
        tspan {
          white-space: pre;
          line-height: 1.4;
        }
        /* Text decoration styles */
        tspan[text-decoration="underline"] {
          text-decoration: underline;
          text-decoration-color: currentColor;
          text-decoration-thickness: 1px;
        }
        /* Highlight styles */
        .highlight {
          background-color: yellow;
          opacity: 0.6;
        }
        /* Link styles */
        .link {
          color: #0066cc;
          text-decoration: underline;
          text-decoration-color: #4da6ff;
        }
        /* Ensure links are clickable */
        a {
          cursor: pointer;
        }
        /* Make sure highlights are visible */
        rect[fill="#ffff00"] {
          opacity: 0.6;
        }
      </style>
    </defs>`);

  // Add a group with the top spacing transform
  svgParts.push(`<g transform="translate(${leftSpacing}, ${topSpacing})">`);
  const layers = stage.getChildren ? stage.getChildren() : (stage.children || []);
  const layerArray = Array.isArray(layers) ? layers : (layers.toArray ? layers.toArray() : []);
  layerArray.forEach((layer) => {
    if (layer && layer.getClassName && layer.getClassName() === "Layer") {
      processNode(layer, svgParts, defsParts);
    }
  });

  // Explicitly add main canvas images from state (ensures images show when node.image() is null or tree differs)
  images.forEach((img) => {
    if (!img) return;
    let href =
      img.imageDataUrl ||
      (img.image && convertImageToDataUrl(img.image)) ||
      (img.src && img.src.startsWith("data:") ? img.src : null);
    if (!href) return;
    const width = img.width ?? 100;
    const height = img.height ?? 100;
    const x = img.x ?? 0;
    const y = img.y ?? 0;
    const rotation = img.rotation ?? 0;
    const offsetX = img.offsetX ?? 0;
    const offsetY = img.offsetY ?? 0;
    const scaleX = img.scaleX ?? 1;
    const scaleY = img.scaleY ?? 1;
    const opacity = img.opacity != null ? img.opacity : 1;
    let transform = `translate(${x - offsetX}, ${y - offsetY})`;
    if (rotation !== 0) transform += ` rotate(${rotation})`;
    if (scaleX !== 1 || scaleY !== 1) transform += ` scale(${scaleX}, ${scaleY})`;
    const refUrlAttr =
      img.referenceUrl != null && img.referenceUrl !== ""
        ? ` data-reference-url="${String(img.referenceUrl).replace(/"/g, "&quot;")}"`
        : "";
    const opacityAttr = opacity !== 1 ? ` opacity="${opacity}"` : "";
    svgParts.push(
      `<g transform="${transform}"><image x="0" y="0" width="${width}" height="${height}" href="${href}"${refUrlAttr}${opacityAttr} /></g>`
    );
  });

  // Process arrows from React state since they are not direct Konva nodes
  // Process regular arrows
  arrows.forEach((arrow) => {
    const {
      points,
      x = 0,
      y = 0,
      stroke: strokeColor = "black",
      fillColor = "black",
      strokeWidth = 2,
      rotation = 0,
      opacity,
    } = arrow;

    // Offset all points by x and y
    const offsetPoints = [];
    for (let i = 0; i < points.length; i += 2) {
      offsetPoints.push(points[i] + x);
      offsetPoints.push(points[i + 1] + y);
    }

    // Build point pairs for polyline
    const pointPairs = [];
    for (let i = 0; i < offsetPoints.length; i += 2) {
      pointPairs.push(`${offsetPoints[i]},${offsetPoints[i + 1]}`);
    }

    // Arrowhead path
    const getArrowPath = (x1, y1, x2, y2, strokeWidth = 2) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = Math.max(10, strokeWidth * 2);
      const arrowWidth = Math.max(5, strokeWidth);
      const tipX = x2;
      const tipY = y2;
      const leftX =
        tipX - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
      const leftY =
        tipY - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
      const rightX =
        tipX - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
      const rightY =
        tipY - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);
      return `M ${tipX},${tipY} L ${leftX},${leftY} L ${rightX},${rightY} Z`;
    };

    const arrowPaths = [];
    if (offsetPoints.length >= 4) {
      const len = offsetPoints.length;
      arrowPaths.push(
        getArrowPath(
          offsetPoints[len - 4],
          offsetPoints[len - 3],
          offsetPoints[len - 2],
          offsetPoints[len - 1],
          strokeWidth
        )
      );
    }

    // No need for transform, points are already offset
    let transform = "";
    if (rotation !== 0) transform = `rotate(${rotation}, ${x}, ${y})`;

    svgParts.push(`<g${transform ? ` transform="${transform}"` : ""}>
        <polyline 
          points="${pointPairs.join(" ")}" 
          stroke="${strokeColor}" 
          stroke-width="${strokeWidth}" 
          fill="none" 
          vector-effect="non-scaling-stroke"
          ${opacity !== undefined ? `opacity="${opacity}"` : ""}
        />
        ${arrowPaths
          .map(
            (path) =>
              `<path d="${path}" fill="${strokeColor}" stroke="none" ${
                opacity !== undefined ? `opacity=\"${opacity}\"` : ""
              } />`
          )
          .join("\n")}
      </g>`);
  });

  doubleSidedArrows.forEach((arrow) => {
    const {
      points,
      x = 0,
      y = 0,
      stroke: strokeColor = "black",
      fillColor = "black",
      strokeWidth = 2,
      rotation = 0,
      opacity,
    } = arrow;

    // Offset all points by x and y
    const offsetPoints = [];
    for (let i = 0; i < points.length; i += 2) {
      offsetPoints.push(points[i] + x);
      offsetPoints.push(points[i + 1] + y);
    }

    // Build point pairs for polyline
    const pointPairs = [];
    for (let i = 0; i < offsetPoints.length; i += 2) {
      pointPairs.push(`${offsetPoints[i]},${offsetPoints[i + 1]}`);
    }

    // Arrowhead path
    const getArrowPath = (x1, y1, x2, y2, strokeWidth = 2) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = Math.max(10, strokeWidth * 2); // pointerLength
      const arrowWidth = Math.max(5, strokeWidth); // pointerWidth / 2
      const tipX = x2;
      const tipY = y2;
      const leftX =
        tipX - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
      const leftY =
        tipY - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
      const rightX =
        tipX - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
      const rightY =
        tipY - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);
      return `M ${tipX},${tipY} L ${leftX},${leftY} L ${rightX},${rightY} Z`;
    };

    const arrowPaths = [];
    if (offsetPoints.length >= 4) {
      const len = offsetPoints.length;
      // Calculate base of arrowhead for polyline
      const x1 = offsetPoints[len - 4];
      const y1 = offsetPoints[len - 3];
      const x2 = offsetPoints[len - 2];
      const y2 = offsetPoints[len - 1];
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const arrowLength = Math.max(10, strokeWidth * 2);
      const baseX = x2 - arrowLength * Math.cos(angle);
      const baseY = y2 - arrowLength * Math.sin(angle);
      // Build point pairs for polyline, replace last point with base
      const pointPairs = [];
      for (let i = 0; i < len - 2; i += 2) {
        pointPairs.push(`${offsetPoints[i]},${offsetPoints[i + 1]}`);
      }
      pointPairs.push(`${baseX},${baseY}`);
      // Arrowhead
      arrowPaths.push(getArrowPath(x1, y1, x2, y2, strokeWidth));
      // Ensure transform is defined here
      let transform = "";
      if (rotation !== 0) transform = `rotate(${rotation}, ${x}, ${y})`;
      svgParts.push(`<g${transform ? ` transform="${transform}"` : ""}>
          <polyline 
            points="${pointPairs.join(" ")}" 
            stroke="${strokeColor}" 
            stroke-width="${strokeWidth}" 
            fill="none" 
            vector-effect="non-scaling-stroke"
            ${opacity !== undefined ? `opacity="${opacity}"` : ""}
          />
          ${arrowPaths
            .map(
              (path) =>
                `<path d="${path}" fill="${strokeColor}" stroke="none" ${
                  opacity !== undefined ? `opacity=\"${opacity}\"` : ""
                } />`
            )
            .join("\n")}
        </g>`);
    }
  });

  // Process text elements directly from the texts array
  texts.forEach((text) => {
    processTextElement(text, svgParts, defsParts, processHtmlContentWithFormatting);
  });

  circles.forEach((circle) => {
    const {
      x,
      y,
      radius,
      fillColor,
      stroke = "black",
      strokeWidth = 2,
      opacity = 1,
      text = "",
      fontSize = 16,
      textColor = "black",
      fontStyle = "normal",
      textDecoration = "",
      align: horizontalAlign = "center",
      verticalAlign = "middle",
      rotation = 0,
      fillOpacity = 1,
      strokeOpacity = 1,
    } = circle;

    // Use the actual fillColor if provided, otherwise default to white
    const fill =
      fillColor !== undefined && fillColor !== null ? fillColor : "white";
    const opacityAttr = opacity !== 1 ? ` opacity="${opacity}"` : "";

    const transform = `translate(${x}, ${y})${
      rotation ? ` rotate(${rotation})` : ""
    }`;

    svgParts.push(
      `<g transform="${transform}">
    <circle cx="0" cy="0" r="${radius}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="none" />
    <circle cx="0" cy="0" r="${radius}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />
  </g>`
    );

    if (text) {
      // Compute text position (processHtmlContentWithFormatting uses leftPadding=15, so we offset)
      let textX = x;
      if (horizontalAlign === "left") {
        textX = x - radius + 4;
      } else if (horizontalAlign === "right") {
        textX = x + radius - 4;
      } else {
        textX = x;
      }

      let textY = y;
      if (verticalAlign === "top") {
        textY = y - radius + 4;
      } else if (verticalAlign === "bottom") {
        textY = y + radius - 4;
      }

      const normalizedText = normalizeShapeHtml(text);
      const contentResult = processHtmlContentWithFormatting(
        normalizedText,
        0,
        fontSize
      );
      if (contentResult.content) {
        svgParts.push(
          `<g transform="translate(${textX - 15}, ${textY}) rotate(${rotation}, 15, 0)">
            ${contentResult.content.replace(/fill="[^"]*"/g, () => `fill="${textColor}"`)}
          </g>`
        );
      } else {
        const isBold = fontStyle.includes("bold");
        const isItalic = fontStyle.includes("italic");
        const fontWeight = isBold ? "bold" : "normal";
        const fontStyleAttr = isItalic ? "italic" : "normal";
        const plainText = typeof document !== "undefined" && document.createElement
          ? (() => {
              const d = document.createElement("div");
              d.innerHTML = text;
              return d.textContent || d.innerText || "";
            })()
          : String(text).replace(/<[^>]*>/g, "");
        if (plainText.trim()) {
          const textAnchor = horizontalAlign === "left" ? "start" : horizontalAlign === "right" ? "end" : "middle";
          const dominantBaseline = verticalAlign === "top" ? "hanging" : verticalAlign === "bottom" ? "text-after-edge" : "middle";
          svgParts.push(
            `<text 
            x="${textX}" 
            y="${textY}" 
            fill="${textColor}" 
            font-size="${fontSize}" 
            font-weight="${fontWeight}" 
            font-style="${fontStyleAttr}" 
            text-decoration="${textDecoration}" 
            text-anchor="${textAnchor}" 
            dominant-baseline="${dominantBaseline}" 
            transform="rotate(${rotation}, ${textX}, ${textY})"
          >${escapeXml(plainText)}</text>`
          );
        }
      }
    }
  });

  rectangles.forEach((rect) => {
    const {
      x,
      y,
      width,
      height,
      fillColor,
      stroke = "black",
      strokeWidth = 2,
      rotation = 0,
      fillOpacity = 1,
      strokeOpacity = 1,
      text = "",
      fontSize = 20,
      textColor = "black",
      fontStyle = "normal",
      textDecoration = "",
      align: horizontalAlign = "center",
      verticalAlign = "middle",
    } = rect;

    // Use the actual fillColor if provided, otherwise default to white
    const fill =
      fillColor !== undefined && fillColor !== null ? fillColor : "white";
    const opacityAttr = fillOpacity !== 1 ? `opacity="${fillOpacity}"` : "";

    const transform = `translate(${x}, ${y})${
      rotation ? ` rotate(${rotation})` : ""
    }`;

    // Draw rectangle
    svgParts.push(
      `<g transform="${transform}">
    <rect x="0" y="0" width="${width}" height="${height}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="none" />
    <rect x="0" y="0" width="${width}" height="${height}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />
  </g>`
    );

    if (text) {
      // Compute text position (processHtmlContentWithFormatting uses leftPadding=15)
      let textX = x + width / 2;
      if (horizontalAlign === "left") {
        textX = x + 8;
      } else if (horizontalAlign === "right") {
        textX = x + width - 8;
      }

      let textY = y + height / 2;
      if (verticalAlign === "top") {
        textY = y + 8;
      } else if (verticalAlign === "bottom") {
        textY = y + height - 8;
      }

      const normalizedText = normalizeShapeHtml(text);
      const contentResult = processHtmlContentWithFormatting(
        normalizedText,
        0,
        fontSize
      );
      if (contentResult.content) {
        svgParts.push(
          `<g transform="translate(${textX - 15}, ${textY}) rotate(${rotation}, 15, 0)">
            ${contentResult.content.replace(/fill="[^"]*"/g, () => `fill="${textColor}"`)}
          </g>`
        );
      } else {
        const plainText = typeof document !== "undefined" && document.createElement
          ? (() => {
              const d = document.createElement("div");
              d.innerHTML = text;
              return d.textContent || d.innerText || "";
            })()
          : String(text).replace(/<[^>]*>/g, "");
        if (plainText.trim()) {
          const textAnchor = horizontalAlign === "left" ? "start" : horizontalAlign === "right" ? "end" : "middle";
          const dominantBaseline = verticalAlign === "top" ? "hanging" : verticalAlign === "bottom" ? "text-after-edge" : "middle";
          const isBold = fontStyle.includes("bold");
          const isItalic = fontStyle.includes("italic");
          const fontWeight = isBold ? "bold" : "normal";
          const fontStyleAttr = isItalic ? "italic" : "normal";
          svgParts.push(
            `<text 
            x="${textX}" 
            y="${textY}" 
            fill="${textColor}" 
            font-size="${fontSize}" 
            font-weight="${fontWeight}" 
            font-style="${fontStyleAttr}" 
            text-decoration="${textDecoration}" 
            text-anchor="${textAnchor}" 
            dominant-baseline="${dominantBaseline}" 
            transform="rotate(${rotation}, ${textX}, ${textY})"
          >${escapeXml(plainText)}</text>`
          );
        }
      }
    }
  });

  // Process videos
  videos.forEach((video) => {
    const { x, y, width, height, src, rotation = 0, opacity = 1 } = video;

    const transform = `translate(${x}, ${y})${
      rotation ? ` rotate(${rotation})` : ""
    }`;

    // Create a video placeholder rectangle with play icon
    svgParts.push(
      `<g transform="${transform}">
          <rect 
            x="${-width / 2}" 
            y="${-height / 2}" 
            width="${width}" 
            height="${height}" 
            fill="#f0f0f0" 
            stroke="#ccc" 
            stroke-width="2"
            opacity="${opacity}"
          />
          <text 
            x="0" 
            y="0" 
            fill="#666" 
            font-size="14" 
            font-family="Arial" 
            text-anchor="middle" 
            dominant-baseline="middle"
          >Video</text>
        </g>`
    );
  });

  svgParts.push(`</g>`);
  svgParts.push(`</svg>`);

  return svgParts.join("\n");
}

